import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@shared/services/api';

interface Organization {
  id: string;
  name: string;
}

interface AdminInfo {
  id: string;
  email: string;
  fullName: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUserInfo: {
    email: string;
    name: string;
    userId: string;
    adminId: string;
    organization: Organization;
    adminInfo: AdminInfo;
    startedAt: string; // Feature 695: ISO timestamp when impersonation started
  } | null;
  setImpersonationState: (
    isImpersonating: boolean,
    userInfo?: {
      email: string;
      name: string;
      userId: string;
      adminId: string;
      organization?: Organization;
      adminInfo?: AdminInfo;
      startedAt?: string; // Feature 695: Timestamp when impersonation started
    }
  ) => void;
  clearImpersonationState: () => void;
  isBannerDismissed: boolean; // Feature 696: Track if banner is dismissed on current route
  dismissBanner: () => void; // Feature 696: Dismiss banner for current route
  currentRoute: string; // Feature 696: Track current route for dismiss state
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

interface ImpersonationProviderProps {
  children: ReactNode;
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUserInfo, setImpersonatedUserInfo] = useState<{
    email: string;
    name: string;
    userId: string;
    adminId: string;
    organization: Organization;
    adminInfo: AdminInfo;
    startedAt: string; // Feature 695: ISO timestamp when impersonation started
  } | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Feature 696: Track banner dismiss state per route
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<string>('');

  // Feature 704: Check impersonation status on app initialization
  // Feature 705: Validate impersonation state with backend API
  // This runs once when the app starts or page reloads
  useEffect(() => {
    const initializeImpersonationState = async () => {
      // Feature 705: First, validate with backend if user is logged in
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          // Call backend to validate impersonation status from token claims
          const status = await authService.getImpersonationStatus();

          if (status.is_impersonating && status.impersonated_user && status.impersonated_by_admin) {
            // Backend confirms active impersonation - sync state
            // Feature 695: Get startedAt from sessionStorage (stored when impersonation began)
            const startedAt =
              sessionStorage.getItem('impersonation_started_at') || new Date().toISOString();

            const userInfo = {
              email: status.impersonated_user.email,
              name: status.impersonated_user.full_name,
              userId: status.impersonated_user.id,
              adminId: status.impersonated_by_admin.id,
              organization: { id: '', name: '' },
              adminInfo: {
                id: status.impersonated_by_admin.id,
                email: status.impersonated_by_admin.email,
                fullName: status.impersonated_by_admin.full_name,
              },
              startedAt,
            };

            // Update sessionStorage to match backend state
            sessionStorage.setItem('is_impersonating', 'true');
            sessionStorage.setItem('impersonated_user_email', userInfo.email);
            sessionStorage.setItem('impersonated_user_name', userInfo.name);
            sessionStorage.setItem('impersonated_user_id', userInfo.userId);
            sessionStorage.setItem('impersonated_by', userInfo.adminId);
            sessionStorage.setItem('impersonated_user_org_id', userInfo.organization.id);
            sessionStorage.setItem('impersonated_user_org_name', userInfo.organization.name);
            // Feature 694: Store admin info
            sessionStorage.setItem('impersonated_by_admin_email', userInfo.adminInfo.email);
            sessionStorage.setItem('impersonated_by_admin_name', userInfo.adminInfo.fullName);
            // Feature 695: Ensure startedAt is stored
            sessionStorage.setItem('impersonation_started_at', startedAt);

            setImpersonatedUserInfo(userInfo);
            setIsImpersonating(true);

            // eslint-disable-next-line no-console
            console.log('[ImpersonationContext] Feature 705: Impersonation validated by backend:', {
              email: userInfo.email,
              name: userInfo.name,
              userId: userInfo.userId,
              adminId: userInfo.adminId,
              organization: userInfo.organization,
            });
          } else {
            // Backend confirms NOT impersonating - clear any stale state
            clearImpersonationStateStorage();
            setImpersonatedUserInfo(null);
            setIsImpersonating(false);

            // eslint-disable-next-line no-console
            console.log(
              '[ImpersonationContext] Feature 705: Backend confirms no active impersonation'
            );
          }

          setIsInitialized(true);
          return; // Skip sessionStorage fallback
        } catch (error) {
          // Feature 705: If API call fails, fall back to sessionStorage
          // This handles offline scenarios and network errors
          // eslint-disable-next-line no-console
          console.warn(
            '[ImpersonationContext] Feature 705: API call failed, falling back to sessionStorage:',
            error
          );
        }
      }

      // Feature 704: Fallback to sessionStorage (also used for non-authenticated pages)
      const isImp = sessionStorage.getItem('is_impersonating') === 'true';

      if (isImp) {
        const email = sessionStorage.getItem('impersonated_user_email') || '';
        const name = sessionStorage.getItem('impersonated_user_name') || '';
        const userId = sessionStorage.getItem('impersonated_user_id') || '';
        const adminId = sessionStorage.getItem('impersonated_by') || '';
        const orgId = sessionStorage.getItem('impersonated_user_org_id') || '';
        const orgName = sessionStorage.getItem('impersonated_user_org_name') || '';
        const adminEmail = sessionStorage.getItem('impersonated_by_admin_email') || '';
        const adminName = sessionStorage.getItem('impersonated_by_admin_name') || '';
        // Feature 695: Get startedAt from sessionStorage
        const startedAt =
          sessionStorage.getItem('impersonation_started_at') || new Date().toISOString();

        setImpersonatedUserInfo({
          email,
          name,
          userId,
          adminId,
          organization: { id: orgId, name: orgName },
          adminInfo: {
            id: adminId,
            email: adminEmail,
            fullName: adminName,
          },
          startedAt,
        });
        setIsImpersonating(true);

        // eslint-disable-next-line no-console
        console.log(
          '[ImpersonationContext] Feature 704: Impersonation state restored from sessionStorage:',
          {
            email,
            name,
            userId,
            adminId,
            organization: { id: orgId, name: orgName },
          }
        );
      } else {
        setImpersonatedUserInfo(null);
        setIsImpersonating(false);
      }

      setIsInitialized(true);
    };

    // Helper to clear storage without triggering state updates
    const clearImpersonationStateStorage = () => {
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('impersonated_user_id');
      sessionStorage.removeItem('impersonated_user_email');
      sessionStorage.removeItem('impersonated_user_name');
      sessionStorage.removeItem('impersonated_by');
      sessionStorage.removeItem('impersonated_user_org_id');
      sessionStorage.removeItem('impersonated_user_org_name');
      sessionStorage.removeItem('original_admin_token');
      // Feature 694: Clear admin info
      sessionStorage.removeItem('impersonated_by_admin_email');
      sessionStorage.removeItem('impersonated_by_admin_name');
      // Feature 695: Clear startedAt timestamp
      sessionStorage.removeItem('impersonation_started_at');
    };

    // Initialize immediately on mount
    initializeImpersonationState();

    // Feature 704: Listen for storage events (for multi-tab sync)
    const handleStorageChange = () => {
      initializeImpersonationState();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Set impersonation state (called when starting impersonation)
  const setImpersonationState = (
    isImp: boolean,
    userInfo?: {
      email: string;
      name: string;
      userId: string;
      adminId: string;
      organization?: Organization;
      adminInfo?: AdminInfo;
      startedAt?: string; // Feature 695: Timestamp when impersonation started
    }
  ) => {
    setIsImpersonating(isImp);

    if (isImp && userInfo) {
      // Feature 695: Use provided startedAt or generate current timestamp
      const startedAtTimestamp = userInfo.startedAt || new Date().toISOString();

      setImpersonatedUserInfo({
        email: userInfo.email,
        name: userInfo.name,
        userId: userInfo.userId,
        adminId: userInfo.adminId,
        organization: userInfo.organization || { id: '', name: '' },
        adminInfo: userInfo.adminInfo || { id: userInfo.adminId, email: '', fullName: '' },
        startedAt: startedAtTimestamp,
      });

      // Store in sessionStorage for page reload persistence
      sessionStorage.setItem('is_impersonating', 'true');
      sessionStorage.setItem('impersonated_user_email', userInfo.email);
      sessionStorage.setItem('impersonated_user_name', userInfo.name);
      sessionStorage.setItem('impersonated_user_id', userInfo.userId);
      sessionStorage.setItem('impersonated_by', userInfo.adminId);
      if (userInfo.organization) {
        sessionStorage.setItem('impersonated_user_org_id', userInfo.organization.id);
        sessionStorage.setItem('impersonated_user_org_name', userInfo.organization.name);
      }
      // Feature 694: Store admin info
      if (userInfo.adminInfo) {
        sessionStorage.setItem('impersonated_by_admin_email', userInfo.adminInfo.email);
        sessionStorage.setItem('impersonated_by_admin_name', userInfo.adminInfo.fullName);
      }
      // Feature 695: Store startedAt timestamp
      sessionStorage.setItem('impersonation_started_at', startedAtTimestamp);

      // eslint-disable-next-line no-console
      console.log('[ImpersonationContext] Impersonation state set:', userInfo);
    } else {
      clearImpersonationState();
    }
  };

  // Clear impersonation state (called when stopping impersonation or logout)
  const clearImpersonationState = () => {
    setIsImpersonating(false);
    setImpersonatedUserInfo(null);
    setIsBannerDismissed(false); // Feature 696: Reset dismiss state
    setCurrentRoute(''); // Feature 696: Clear route

    // Clear all impersonation-related sessionStorage items
    sessionStorage.removeItem('is_impersonating');
    sessionStorage.removeItem('impersonated_user_id');
    sessionStorage.removeItem('impersonated_user_email');
    sessionStorage.removeItem('impersonated_user_name');
    sessionStorage.removeItem('impersonated_by');
    sessionStorage.removeItem('impersonated_user_org_id');
    sessionStorage.removeItem('impersonated_user_org_name');
    sessionStorage.removeItem('original_admin_token');
    // Feature 694: Clear admin info
    sessionStorage.removeItem('impersonated_by_admin_email');
    sessionStorage.removeItem('impersonated_by_admin_name');
    // Feature 695: Clear startedAt timestamp
    sessionStorage.removeItem('impersonation_started_at');
    // Feature 696: Clear banner dismiss state
    sessionStorage.removeItem('impersonation_banner_dismissed_route');

    // eslint-disable-next-line no-console
    console.log('[ImpersonationContext] Impersonation state cleared');
  };

  // Feature 696: Dismiss banner for current route
  const dismissBanner = () => {
    setIsBannerDismissed(true);
    // Store dismissed route in sessionStorage so it persists across remounts
    if (currentRoute) {
      sessionStorage.setItem('impersonation_banner_dismissed_route', currentRoute);
    }
  };

  // Don't render children until initialization is complete
  // This ensures the impersonation state is read before first render
  if (!isInitialized) {
    return null;
  }

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        impersonatedUserInfo,
        setImpersonationState,
        clearImpersonationState,
        isBannerDismissed, // Feature 696: Provide dismiss state
        dismissBanner, // Feature 696: Provide dismiss function
        currentRoute, // Feature 696: Provide current route
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}
